import {useRouter} from 'next/router'
import {useCallback, useEffect, useRef} from 'react'
// import {setCookie} from 'cookies-next'
import {sendGTMEvent} from '@next/third-parties/google'
import type {CdcUser} from '@elanco/gateway-utils'
import {env} from '@/services/env/client'
import type {
	AdvancedPageTemplateContentItem,
	ArticleConfigContentItem,
	GlobalConfigContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import {useAuth} from '@/products/auth/auth-provider'
import type {JsonObject} from '@/products/auth/cdc-screensets/models'

/** Helper function to push data layer events. Use this instead of window.dataLayer.push(...) */
export const pushToDataLayer = (data: Record<string, unknown>): void => {
	sendGTMEvent(data)
}

// product information
const getPageData = (
	page: Tersed<AdvancedPageTemplateContentItem>
): {
	productName: string
	animal: string[]
} => {
	const pageConfig = page.elements.config[0]

	let animal: string[] = []

	if (pageConfig && 'species' in pageConfig.elements) {
		const articleConfig = pageConfig as Tersed<ArticleConfigContentItem>
		animal = articleConfig.elements.species.length
			? articleConfig.elements.species.map(({elements: {name}}) =>
					name.trim()
				)
			: ['(not set)']
	}

	let productName = '(not set)'

	if (
		pageConfig?.system.type === 'config_product' ||
		pageConfig?.system.type === 'config_brand'
	) {
		productName = pageConfig.system.name.replace(/[\r\n]+/g, '')
	}

	return {
		productName,
		animal,
	}
}

const pageEvents = (
	page: Tersed<AdvancedPageTemplateContentItem>,
	userId: string | null | undefined,
	locale: string
): void => {
	const pageViewData: Record<string, unknown> = {
		event: 'page_view',
		country: locale,
		page_template: page.system.type,
		portal: env.NEXT_PUBLIC_TOWER,
		...getPageData(page),
	}

	if (userId === null) {
		// Explicitly send null if user was logged in but has logged out
		pageViewData.user_id = null
	} else if (typeof userId === 'string' && userId.trim() !== '') {
		// Send user ID only if it’s a non-empty string
		pageViewData.user_id = userId
	}
	// Else: do NOT include user_id at all

	pushToDataLayer(pageViewData)

	if (
		page.system.type === 'article' ||
		page.system.type === 'article_v1' ||
		page.system.type === 'article_v2' ||
		page.system.type === 'article_v3'
	) {
		pushToDataLayer({
			event: 'article_info',
			page_template: page.system.type,
			article_name: page.system.name,
			...getPageData(page),
		})
	}
}
const errordataLayer = (): void => {
	pushToDataLayer({
		event: 'error_404',
		page_url: window.location.href,
	})
}

const pageviewLastSent = new Map<string, 'anon' | 'known' | 'logout'>()

const sendPageViewIfNeededModule = (
	page: Tersed<AdvancedPageTemplateContentItem>,
	slug: string,
	userId: string | null | undefined,
	locale: string
): void => {
	let type: 'anon' | 'known' | 'logout'
	if (userId === null) {
		type = 'logout'
	} else if (typeof userId === 'string' && userId.trim() !== '') {
		type = 'known'
	} else {
		type = 'anon'
	}
	const last = pageviewLastSent.get(slug)
	if (last === type) return

	pageEvents(page, userId, locale)
	pageviewLastSent.set(slug, type)
}

export const sendScreenSetEventToDataLayer = (
	response: JsonObject,
	formType: string,
	blockCodename = '',
	campaignId = ''
): void => {
	const {errorCode, socialProviders, newUser, UID: userId = ''} = response
	const statusCode = Number(errorCode)

	const SCREENSET_IDS = {
		LOGIN: 'gigya-login-form',
		REGISTER: 'gigya-register-form',
		RESET_PASSWORD: 'gigya-reset-password-form',
		SOCIAL: 'div.gigya-social-login',
		SIGNUP_CAMPAIGN: 'gigya-subscribe-with-email-form',
	}

	const statusCodes = [
		206002, // Account created pending email verification
		0, // OK valid response
	]

	const provider: Record<string, string> = {
		site: 'Email',
		facebook: 'Facebook',
		googleplus: 'Google',
	}

	const status = statusCodes.includes(statusCode)
		? 'Successful'
		: 'Unsuccessful'

	switch (formType) {
		case SCREENSET_IDS.LOGIN: {
			pushToDataLayer({
				event: 'login',
				action: 'Login',
				status,
				method: provider.site,
				user_id: userId,
			})

			break
		}
		case SCREENSET_IDS.SOCIAL: {
			if (statusCodes.includes(statusCode) && socialProviders) {
				if (newUser) {
					pushToDataLayer({
						event: 'signup_completed',
						action: 'Registration Completed',
						method: provider[socialProviders],
						user_id: userId,
					})
				} else {
					pushToDataLayer({
						event: 'login',
						action: 'Login',
						status,
						method: provider[socialProviders],
						user_id: userId,
					})
				}
			}
			break
		}
		case SCREENSET_IDS.REGISTER: {
			pushToDataLayer({
				event: 'signup_initiated',
				action: 'Registration Initiated',
				status,
				method: provider.site,
				user_id: userId,
			})
			break
		}
		case SCREENSET_IDS.RESET_PASSWORD: {
			pushToDataLayer({
				event: 'login',
				action: 'Login',
				status:
					status === 'Successful'
						? 'Forgot Password'
						: 'Unsuccessful',
				method: 'Email',
			})

			break
		}
		case SCREENSET_IDS.SIGNUP_CAMPAIGN: {
			const submitButtons =
				document.getElementsByClassName('gigya-input-submit')

			const buttonElements = Array.from(submitButtons)
			const valuesArray: string[] = []

			if (buttonElements.length > 0) {
				buttonElements.forEach((ele) => {
					if (ele instanceof HTMLInputElement) {
						valuesArray.push(ele.value)
					}
				})
			}

			if (blockCodename === 'cdc_screen_set_wrapper') {
				pushToDataLayer({
					event: 'signup_campaign',
					action: 'Signup Completed',
					status,
					campaign: campaignId || '',
					user_id: userId,
				})
				if (valuesArray.length > 0) {
					pushToDataLayer({
						event: 'cta_click',
						cta_name: valuesArray[valuesArray.length - 1],
						cta_category: 'CTA_Button',
					})
				}
			}

			break
		}
		default:
	}
}

const getScrollDepth = (): number => {
	const rootNode = document.documentElement
	const documentBody = document.body

	const scrollDepth =
		((rootNode.scrollTop || documentBody.scrollTop) /
			((rootNode.scrollHeight || documentBody.scrollHeight) -
				rootNode.clientHeight)) *
		100

	return Math.round(scrollDepth)
}

const useScrollDepthDataLayer = (slug: string): void => {
	const thresholdsRef = useRef<number[]>([25, 50, 75, 100])
	const handleScroll = useCallback((): void => {
		const thresholds = thresholdsRef.current

		if (thresholds.length === 0) {
			window.removeEventListener('scroll', handleScroll)
			return
		}

		const scrollDepth = getScrollDepth()

		thresholds.forEach((threshold) => {
			if (scrollDepth >= threshold && scrollDepth <= threshold + 25) {
				pushToDataLayer({
					event: 'scroll_depth',
					scroll_depth_threshold: String(threshold),
				})

				thresholdsRef.current = thresholdsRef.current.filter(
					(item) => item !== threshold
				)
			}
		})
	}, [])

	useEffect(() => {
		thresholdsRef.current = [25, 50, 75, 100]
		window.addEventListener('scroll', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [slug, handleScroll])
}

const addDataLayerEventsToLinks = (ele: MouseEvent): void => {
	let targetElement = ele.target
	while (targetElement && !(targetElement instanceof HTMLAnchorElement)) {
		const targetNode = targetElement as HTMLElement
		targetElement = targetNode.parentNode
	}
	if (targetElement instanceof HTMLAnchorElement) {
		if (targetElement.href !== '' && targetElement.innerText.trim()) {
			if (targetElement.hostname === window.location.hostname) {
				pushToDataLayer({
					event: 'internal_link_click',
					element_text: targetElement.innerText,
					link_url: targetElement.href,
				})
			} else if (
				targetElement.hostname === 'www.elancolabels.com' ||
				targetElement.hostname === 'elancolabels.com' ||
				targetElement.href.endsWith('.pdf')
			) {
				pushToDataLayer({
					event: 'pdf_download',
					element_text: targetElement.innerText,
					link_url: targetElement.href,
				})
			} else {
				pushToDataLayer({
					event: 'external_link_click',
					element_text: targetElement.innerText,
					link_url: targetElement.href,
				})
			}
		}
	}
}
const handleLinkClick = (event: MouseEvent): void => {
	const clickedElement = event.target as HTMLElement

	if (
		clickedElement.tagName.toLowerCase() === 'a' ||
		clickedElement.closest('a') !== null
	) {
		addDataLayerEventsToLinks(event)
	}
}

const addinternalAndExternalLinkEvents = (): void => {
	document.body.addEventListener('click', handleLinkClick)
}

const addSignUpCompletedFromMailDataLayer = (userId: string | null): void => {
	pushToDataLayer({
		event: 'signup_completed',
		user_id: userId,
		action: 'Registration Completed',
		method: 'Email',
	})
}

const cleanUpInternalAndExternalLinkEvents = (): void => {
	document.body.removeEventListener('click', handleLinkClick)
}

export function useAnalytics(
	page: Tersed<AdvancedPageTemplateContentItem>,
	globalConfig: Tersed<GlobalConfigContentItem>
): void {
	const {locale = '', ...router} = useRouter()
	// if (getConsent() === 'permit 1,2,3') SearchCookieComponent()
	const authState = useAuth()
	const userId = authState.userDetails.id
	const slug = page.elements.pageUrl
	useScrollDepthDataLayer(slug)

	const sendPageViewIfNeeded = useCallback(
		(intendedUserId: string | null | undefined): void => {
			sendPageViewIfNeededModule(page, slug, intendedUserId, locale)
		},
		[page, slug, locale]
	)

	const handlePageViewEvent = useCallback(
		(accountInfo: CdcUser): void => {
			const hasLoggedInBefore =
				sessionStorage.getItem('hasLoggedInBefore') === 'true'

			if (accountInfo.UID) {
				sessionStorage.setItem('hasLoggedInBefore', 'true')
			}

			let intendedUserId: string | null | undefined
			if (accountInfo.UID) {
				intendedUserId = accountInfo.UID
			} else if (hasLoggedInBefore) {
				intendedUserId = null
			} else {
				intendedUserId = undefined
			}

			sendPageViewIfNeeded(intendedUserId)
		},
		[sendPageViewIfNeeded]
	)

	useEffect(() => {
		addinternalAndExternalLinkEvents()
		if (router.query.errorCode === '0') {
			addSignUpCompletedFromMailDataLayer('unknown')
		}
		if (slug === '/404') errordataLayer()

		return () => {
			cleanUpInternalAndExternalLinkEvents()
		}
	}, [slug, router.query.errorCode])

	useEffect(() => {
		if (router.query.errorCode === '206005' && userId !== null) {
			addSignUpCompletedFromMailDataLayer(userId)
		}
	}, [userId, router.query.errorCode])

	useEffect(() => {
		if (authState.ready) {
			window.gigya?.accounts.getAccountInfo({
				callback: handlePageViewEvent,
			})
		} else if (!globalConfig.elements.cdcKey) {
			sendPageViewIfNeeded(undefined)
		}
	}, [
		authState.ready,
		handlePageViewEvent,
		sendPageViewIfNeeded,
		globalConfig.elements.cdcKey,
	])
}
